import { useEffect, useState } from "react";
import { Button } from "reactstrap";
import { Modal } from "rsuite";

import RecordNameCell from "../../../components/Records/RecordNameCell";
import useRecord from "../../../api/api-hooks/records/useRecord";
import SkeletonLoader from "../../../elements/SkeletonLoader";
import { FileSources } from "../../../types/FileInterfaces";

interface PreviewSourceRecordsProps {
  open: boolean;
  onClose: () => void;
  fileName: string;
  sources: FileSources[];
  handleSourcesUpdate: (newSources: FileSources[]) => void;
}

function PreviewSourceRecords({
  open,
  onClose,
  fileName,
  sources,
  handleSourcesUpdate,
}: PreviewSourceRecordsProps) {
  const [fileSources, setFileSources] = useState<FileSources[]>(sources);

  useEffect(() => {
    setFileSources(sources);
  }, [sources]);

  function handleClose() {
    onClose();
    setFileSources([]);
  }

  function onDeleteSource(recordID: string, moduleAPIName: string) {
    const newSources = fileSources.flatMap((s) => {
      if (s.SourceID === recordID && s.SourceModule === moduleAPIName)
        return [];
      return [s];
    });

    setFileSources(newSources);
    handleSourcesUpdate(newSources);
  }

  return (
    <Modal open={open} onClose={handleClose} size="sm">
      <Modal.Header className="tw:border-b-1 tw:border-lighter">
        <Modal.Title className="tw:pt-3 tw:pl-3 h2">
          {fileName || "(No Name)"}
        </Modal.Title>
        <p className="tw:text-sm tw:text-dark tw:pl-3 tw:mb-3">
          Records using this file:
        </p>
      </Modal.Header>
      <Modal.Body className="tw:mx-4">
        <div>
          {fileSources.map((s) => {
            return (
              <SourceRecordBody
                key={s.SourceID}
                recordID={s.SourceID}
                moduleAPIName={s.SourceModule}
                onDeleteSource={onDeleteSource}
              />
            );
          })}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function SourceRecordBody({
  recordID,
  moduleAPIName,
  onDeleteSource,
}: {
  recordID: string;
  moduleAPIName: string;
  onDeleteSource: (recordID: string, moduleAPIName: string) => void;
}) {
  const { record, recordLoading } = useRecord({ moduleAPIName, recordID });

  if (recordLoading) return <SkeletonLoader css="tw:m-0 tw:p-0" />;

  if (!record) {
    return (
      <div className="tw:flex tw:justify-between">
        <span className="tw:text-red-500 tw:font-bold">Record not found</span>
        <Button
          size="sm"
          color="primary"
          onClick={() => onDeleteSource(recordID, moduleAPIName)}
        >
          Delete Source
        </Button>
      </div>
    );
  }

  return (
    <RecordNameCell
      record={record}
      onOpen={null}
      commentsWidget={false}
      imageField={null}
      imageFieldPresentation={null}
      metadataField={null}
      metadataFieldType={null}
      disableViewRecord={false}
      className="tw:pb-3"
    />
  );
}

export default PreviewSourceRecords;
